/**
 * RefineTabContent — Refine tab for the UnifiedBottomPanel.
 *
 * RichTextBox feedback area with @ mention support and a built-in char counter.
 * Bottom control bar:
 *   Spacer | Retry pill (popover) | Sparks | Generate button
 *
 * Style selector removed — refine mode uses only the previous image + feedback prompt.
 */

import { memo, useState, useCallback, useEffect, useRef } from 'react'
import { useReactFlow } from 'reactflow'
import { RichTextBox, type RichTextBoxState } from './RichTextBox'
import { CREATIVE_TEXT_MAX_LENGTH, logCreativeTextLimitConfigured } from './descriptionLimits'
import { useReadOnly } from '../../../../contexts/ReadOnlyContext'
import type { ReactFlowCanvasEdgeData, ReactFlowNodeData } from '../../../../hooks/useYCanvas'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const FEEDBACK_MAX_LENGTH = CREATIVE_TEXT_MAX_LENGTH

/** Default sparks per image when pricing is unavailable */
const BASE_SPARKS = 3

/** Node-type-specific placeholder text for the feedback textarea */
export const REFINE_PLACEHOLDERS: Record<string, string> = {
  characterDesigner: 'e.g. make hair darker, add details to outfit...',
  locationDesigner: 'e.g. warmer atmosphere, add ambient lighting...',
  objectDesigner: 'e.g. add more detail, change the material...',
  imageGenerator: 'e.g. make it brighter, add more contrast...',
  storyboardDesigner: 'e.g. change the camera angle, more dramatic lighting...',
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StyleOption {
  id: string
  name: string
}

export interface RefineTabContentProps {
  /** Current feedback text */
  feedbackText: string
  /** Feedback text change callback */
  onFeedbackTextChange: (text: string) => void
  /** Submit callback — receives retry count */
  onSubmit: (retryCount?: number) => void
  /** Whether the node is currently running */
  isRunning: boolean
  /** Node type for placeholder text */
  nodeType?: string
  /** Optional custom placeholder override */
  placeholder?: string
  /** Available styles for the inline dropdown */
  styles?: StyleOption[]
  /** Currently selected style ID */
  selectedStyleId?: string
  /** Style selection callback */
  onStyleChange?: (styleId: string) => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RefineTabContent = memo(function RefineTabContent({
  feedbackText,
  onFeedbackTextChange,
  onSubmit,
  isRunning,
  nodeType,
  placeholder,
  selectedStyleId: _selectedStyleId,
  onStyleChange: _onStyleChange,
}: RefineTabContentProps) {
  const readOnly = useReadOnly()
  const [retryCount, setRetryCount] = useState(1)
  const [isRetryPopoverOpen, setIsRetryPopoverOpen] = useState(false)

  const retryPillRef = useRef<HTMLButtonElement>(null)
  const retryPopoverRef = useRef<HTMLDivElement>(null)
  const { getNodes } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()

  const sparksCount = retryCount * BASE_SPARKS

  useEffect(() => {
    logCreativeTextLimitConfigured('refine-tab-feedback', { nodeType })
  }, [nodeType])

  const resolvedPlaceholder =
    placeholder ??
    (nodeType ? REFINE_PLACEHOLDERS[nodeType] : undefined) ??
    "Describe the changes you'd like to make..."

  // ── Click-outside for retry popover ───────────────────────────────────────
  useEffect(() => {
    if (!isRetryPopoverOpen) return

    const handlePointerDown = (e: PointerEvent) => {
      if (!(e.target instanceof HTMLElement)) return
      const target = e.target
      if (
        retryPopoverRef.current &&
        !retryPopoverRef.current.contains(target) &&
        retryPillRef.current &&
        !retryPillRef.current.contains(target)
      ) {
        setIsRetryPopoverOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isRetryPopoverOpen])

  const handleRichTextChange = useCallback(
    (state: RichTextBoxState) => {
      if (state.plainText.length <= FEEDBACK_MAX_LENGTH) {
        onFeedbackTextChange(state.plainText)
      }
    },
    [onFeedbackTextChange]
  )

  const handleGenerate = useCallback(() => {
    onSubmit(retryCount)
  }, [onSubmit, retryCount])

  return (
    <div className="h-full flex flex-col" data-testid="refine-tab-content">
      {/* Feedback — RichTextBox with @ mention support */}
      <div className="p-3 flex-1">
        <RichTextBox
          containerClassName="w-full h-full bg-surface-2 border border-border-subtle rounded-lg px-3 py-2 transition-colors focus-within:border-primary/50 nodrag nowheel"
          containerTestId="refine-feedback-textarea"
          canvasNodes={getNodes()}
          placeholder={resolvedPlaceholder}
          initialContent={feedbackText}
          onContentChange={handleRichTextChange}
          onSubmit={() => {
            if (!isRunning && feedbackText.trim()) handleGenerate()
          }}
          minHeight={40}
          maxHeight={120}
          maxLength={FEEDBACK_MAX_LENGTH}
          disabled={readOnly}
        />
      </div>

      {/* Control Bar — hidden in read-only mode */}
      {readOnly ? null : <div className="px-3 py-2 border-t border-border-subtle flex items-center gap-2" data-testid="refine-control-bar">
        {/* Spacer */}
        <div className="flex-1" />

        {/* Retry Count — Collapsed Pill with Popover */}
        <div className="relative" data-testid="refine-retry-selector">
          <button
            ref={retryPillRef}
            onClick={() => setIsRetryPopoverOpen(!isRetryPopoverOpen)}
            className="px-2.5 py-1 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
            data-testid="refine-retry-pill"
          >
            {retryCount}x
          </button>

          {isRetryPopoverOpen && (
            <div
              ref={retryPopoverRef}
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-surface-1 border border-border rounded-lg shadow-2xl overflow-hidden z-50 min-w-[48px]"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              data-testid="refine-retry-popover"
            >
              {([4, 3, 2, 1]).map((n) => (
                <button
                  key={n}
                  onClick={() => {
                    setRetryCount(n)
                    setIsRetryPopoverOpen(false)
                  }}
                  className={`w-full px-3 py-1.5 text-xs font-medium text-center transition-colors ${
                    retryCount === n
                      ? 'text-primary-foreground'
                      : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                  }`}
                  style={
                    retryCount === n
                      ? { backgroundColor: 'var(--primary)' }
                      : undefined
                  }
                  data-testid={`refine-retry-${n}x`}
                >
                  {n}x
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sparks Count */}
        <span className="flex items-center gap-1 text-xs text-text-muted tabular-nums" data-testid="refine-sparks-count">
          <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
          </svg>
          {sparksCount}
        </span>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={isRunning || !feedbackText.trim()}
          className="w-8 h-8 flex items-center justify-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: 'var(--primary)' }}
          title="Generate"
          data-testid="refine-generate-button"
        >
          <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>}
    </div>
  )
})

RefineTabContent.displayName = 'RefineTabContent'

export { RefineTabContent }
